import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";
import GVLeftArrow from "@tSurya/theme/styleguide/icons/GVLeftArrow";
import GVRightArrow from "@tSurya/theme/styleguide/icons/GVRightArrow";

import {
    COLORS as SURYA_COLORS,
    COMMON as SURYA_COMMON,
    ELEMENTS as SURYA_ELEMENTS,
    FORM,
    MENU,
    PRODUCT,
    SPACING,
    TYPOGRAPHY as SURYA_TYPOGRAPHY,
} from "@tSurya/theme/design.surya";

const COLORS = {
    ...SURYA_COLORS,
    primary: {
        base: "#1C1917",
        contrast: "#ffffff",
    },
    secondary: {
        base: "#EBE5DF",
        contrast: "#228B22",
    },
    accent: {
        main: "#494745",
        contrast: "#FFFFFF",
    },
    success: "#336633",
    warning: "#BE6A14",
    error: "#A93326",
    background: {
        ...SURYA_COLORS.background,
        light: "#FAF8F5",
    },
    border: {
        main: "#E0E0E0",
    },
    disabled: {
        main: "#CCCCCC",
    },
    info: {
        main: "#1C355E",
        contrast: "#FFFFFF",
    },
    snow: "#FAF8F5",
    aegean: "#253242",
    text: {
        ...SURYA_COLORS.text,
        accent: "#494745",
        disabled: "#C5C5C7",
        main: "#1C1917",
        link: "#A45D44",
        white: "#FFFFFF",
    },
    input: {
        background: "#FAF8F5",
    },
    filterIcon: {
        primary: {
            base: "#494745",
            contrast: "#FFFFFF",
        },
    },
    heartIcon: {
        color: "#1c1917",
        fill: "#ffffffff",
    },
};

const TYPOGRAPHY = {
    ...SURYA_TYPOGRAPHY,
    fontFamilyImportUrl: "/staticContent/globalviews/fonts/gv-fonts.css",
    fontFamily: {
        title: `'Lato', sans-serif`,
        base: `'Lato', sans-serif`,
        heading: `'Fraunces-72pt', serif`,
    },
    breadcrumbs: {
        weight: 500,
        current: {
            weight: 500,
        },
    },
    body: {
        size: "15px",
        weight: 400,
        fontFamily: "'Lato', sans-serif",
        lineHeight: "24px",
        letterSpacing: "0.5px",
    },
    p: {
        size: "16px",
        weight: 400,
        fontFamily: "'Lato', sans-serif",
        lineHeight: "24px",
        letterSpacing: "0.5px",
    },
    h1: {
        size: 40,
        weight: 100,
        italic: true,
        lineHeight: "60px",
        fontFamily: "'Fraunces-72pt', serif",
        fontStyle: "italic",
        css: css`
            ${({ theme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: 32px;
                        line-height: 48px;
                    `,
                    null,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    h2: {
        size: 32,
        weight: 100,
        italic: true,
        lineHeight: "48px",
        fontFamily: "'Fraunces-72pt', serif",
        fontStyle: "italic",
    },
    h3: {
        size: 28,
        weight: 100,
        italic: true,
        lineHeight: "42px",
        fontFamily: "'Fraunces-72pt', serif",
        fontStyle: "italic",
    },
    h4: {
        size: 24,
        weight: 100,
        italic: true,
        lineHeight: "36px",
        fontFamily: "'Fraunces-72pt', serif",
        fontStyle: "italic",
    },
    h5: {
        size: 20,
        weight: 100,
        italic: true,
        lineHeight: "30px",
        fontFamily: "'Fraunces-72pt', serif",
        fontStyle: "italic",
    },
    h6: {
        size: 16,
        weight: 100,
        italic: true,
        lineHeight: "24px",
        fontFamily: "'Fraunces-72pt', serif",
        fontStyle: "italic",
    },
    headerPrimary: {
        size: 16,
        fontFamily: "'Lato', serif",
        color: "text.main",
        transform: "none" as const,
        weight: 400,
        lineHeight: 1.5,
    },
    headerSecondary: {
        size: 16,
        fontFamily: "'Lato', serif",
        transform: "none" as const,
        weight: 400,
        lineHeight: 1.5,
    },
    headerTertiary: {
        size: 16,
        fontFamily: "'Lato', serif",
        color: "text.main",
        transform: "uppercase" as const,
        lineHeight: 1.5,
        weight: 400,
    },
    legend: {
        size: 16,
        color: "text.accent",
        transform: "uppercase" as const,
        weight: 700,
    },
    link: {
        size: 16,
        weight: 600,
        lineHeight: "24px",
    },
    header: {
        size: "16px",
        textTransform: "capitalize",
        letterSpacing: "0.5px",
        lineHeight: "24px",
        weight: 500,
    },
    openTradeAccountLabel: {
        textTransform: "none",
    },
    cartLink: {
        size: "16px",
        textTransform: "capitalize",
        weight: 500,
    },
    signInTitle: {
        size: "16px",
        textTransform: "capitalize",
        weight: 500,
    },
    megamenuAccentLink: {
        weight: 600,
    },
    megaMenuHeading: {
        size: "16px",
        weight: 700,
        letterSpacing: "normal",
        lineHeight: "22px",
        textTransform: "none" as const,
        color: COLORS.text.accent,
    },
    megaMenuLink: {
        size: "16px",
        weight: 400,
        letterSpacing: "normal",
        lineHeight: "24px",
        color: COLORS.text.accent,
    },
    footerHeadings: {
        textTransform: "capitalize",
        size: "28px",
        weight: 100,
        fontFamily: "'Fraunces-72pt', serif",
        fontStyle: "italic",
        lineHeight: "42px",
    },
    footerLinks: {
        textTransform: "uppercase",
        color: getColor("primary.contrast"),
        letterSpacing: "1px",
    },
    footerEndLine: {
        size: "12px",
        weight: 500,
        lineHeight: "18px",
        fontFamily: "'Lato', serif",
        fontStyle: "normal",
        letterSpacing: "0.24px",
        color: getColor("primary.contrast"),
        opacity: 1,
    },
    mobileBrandNavigationLink: {
        size: "12px",
    },
    mobileNavCategoryLinks: {
        size: "16px",
        weight: 500,
        lineHeight: "24px",
        letterSpacing: "normal",
    },
    mobileViewLinksList: {
        size: "16px",
        weight: 500,
        lineHeight: "24px",
    },
    button: {
        size: "16px",
        weight: 500,
        lineHeight: "24px",
        fontFamily: "'Lato', sans-serif",
    },
    pageTitle: {
        size: "32px",
        weight: 100,
        lineHeight: "48px",
        fontStyle: "italic",
        fontFamily: "'Fraunces-72pt', serif",
        textTransform: "capitalize",
    },
    headings: {
        weight: 100,
        fontStyle: "italic",
        fontFamily: "'Fraunces-72pt', serif",
        textTransform: "capitalize",
    },
    widgetTitle: {
        size: "40px",
        lineHeight: 1.5,
        weight: 100,
        textTransform: "capitalize",
        fontStyle: "italic",
        fontFamily: "'Fraunces-72pt', serif",
        mobileSize: "32px",
        mobileLineHeight: "48px",
    },
    badge: {
        size: "16px",
        lineHeight: "24px",
        weight: 400,
        fontFamily: "'Lato', sans-serif",
        textTransform: "uppercase",
    },
    heroCarousel: {
        size: "52px",
        lineHeight: "68px",
        fontFamily: "'Fraunces-72pt', serif",
        fontStyle: "italic",
        mobileSize: "32px",
        mobileLineHeight: "42px",
    },
    cardSectionTitle: {
        size: "24px",
    },
    filterButton: {
        size: "16px",
        weight: 400,
        lineHeight: "24px",
        fontFamily: "'Lato', sans-serif",
        letterSpacing: "0.5px",
    },
    sortSelectLabel: {
        size: "16px",
        weight: 500,
        lineHeight: "24px",
        fontFamily: "'Lato', sans-serif",
        letterSpacing: "0.5px",
    },
    sortSelectInput: {
        size: "16px",
        weight: 500,
        lineHeight: "24px",
        fontFamily: "'Lato', sans-serif",
        letterSpacing: "0.5px",
    },
    topCategoryFilter: {
        size: "16px",
        weight: 500,
        fontFamily: "'Lato', sans-serif",
        lineHeight: "24px",
        letterSpacing: "0.5px",
        color: COLORS.text.main,
    },
    productCard: {
        brand: {
            size: "16px",
            weight: 600,
            fontFamily: "'Lato', sans-serif",
            lineHeight: "24px",
            letterSpacing: "0.5px",
        },
        linkTitle: {
            weight: 600,
        },
    },
    accordionItem: {
        size: "16px",
        weight: 400,
        lineHeight: "24px",
        fontFamily: "'Lato', sans-serif",
        textTransform: "capitalize",
        sizeSmaller: "16px",
    },
    categoryDetailsLinksList: {
        weight: 500,
        size: "16px",
    },
    categoryCard: {
        name: {
            size: "16px",
            weight: 600,
        },
    },
    productDetails: {
        variantButton: {
            activeWeight: 500,
            size: "16px",
        },
        inventoryMessage: {
            weight: 600,
        },
        accordionButton: {
            fontFamily: "'Lato', sans-serif",
            size: "14px",
            weight: 400,
            lineHeight: "24px",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: "#494745",
        },
        addToCartButton: {
            size: "16px",
            weight: 600,
            letterSpacing: "normal",
            fontFamily: "'Lato', sans-serif",
            lineHeight: "24px",
            fontWeight: 600,
        },
        priceText: {
            size: 20,
            weight: 600,
        },
    },
};

const ELEMENTS = {
    button: {
        base: {
            padding: "12px 20px",
            borderRadius: "2px",
        },
        link: {
            letterSpacing: "1px",
        },
    },
    radio: {
        size: "20px",
        dotSize: "10px",
        dotOffset: "5px",
        borderColor: COLORS.primary.base,
        checkedBackground: COLORS.primary.base,
    },
    selectIconSize: "14px",
    cartLink: {
        height: "unset",
    },
    logo: {
        maxWidth: "300px",
    },
    navigationItemDesktop: {
        padding: "16px 8px",
    },
    megamenu: {
        boxShadow: "0 5px 12px 0 rgba(0, 0, 0, 0.10)",
        padding: "50px 15px",
        gridPadding: "16px",
        columnGap: "20px",
        justifyContent: "flex-start",
    },
    menuItem: {
        marginBottom: "8px",
        marginTop: "8px",
    },
    carouselArrows: {
        size: "36px",
        boxShadow: "0 1px 3px 0 rgba(#A6AFC3, 0.40)",
        border: "1px solid #F5F5F5",
        padding: "20px",
        iconTop: "0",
    },
    footerLinks: {
        marginBottom: "10px",
    },
    formField: {
        borderRadius: "2px",
    },
    filterButton: {
        padding: "8px 16px",
        border: "1px solid #E0E0E0",
        borderRadius: "2px",
        height: "40px",
        borderColor: "#E0E0E0",
    },
    sortSelect: {
        height: "40px",
        marginRight: "0",
    },
    accordionContainer: {
        desktop: {
            paddingLeft: "0",
            marginTop: "20px",
        },
        mobile: {
            paddingLeft: "20px",
        },
    },
    accordionItem: {
        borderBottom: "1px solid #F1EFEA !important",
    },
    filterCheckbox: {
        checkedBackground: "#1C1917",
    },
    cardContainerStyles: {
        cardDivider: {
            width: "100%",
        },
    },
    categoryDetailsLinksList: {
        paddingLeft: "0",
    },
    gridViewClickable: {
        marginRight: "0",
    },
    cardContainerMultiColumnGridItem: {
        padding: "10px",
        paddingLeft: "10px",
    },
    cardList: {
        gridContainerOffset: {
            marginLeft: "-10px",
            marginRight: "-10px",
            width: "calc(100% + 20px)",
            mobile: {
                marginLeft: "0",
                marginRight: "0",
                width: "100%",
            },
            swatchMobile: {
                marginLeft: "-7px",
                marginRight: "-7px",
                width: "calc(100% + 14px)",
            },
        },
    },
    productListing: {
        heading: {
            marginBottom: "16px",
        },
        totalSortContainer: {
            marginTop: "0",
        },
    },
    productDetails: {
        variantButton: {
            padding: "8px 16px",
            borderRadius: "2px",
            activeBorder: "1px solid #1C1917",
            activeBackground: "#FAF8F5",
        },
        accordionSection: {
            marginBottom: "0",
            marginBottomMobile: "0",
        },
        accordionButton: {
            backgroundColor: "#FFFFFF",
            padding: "16px 0",
            expandedBorderBottom: "1px solid transparent",
            borderTop: "1px solid #E0E0E0",
            borderBottom: "none",
            borderLeft: "none",
            borderRight: "none",
        },
        accordionPanel: {
            paddingBottom: "16px",
        },
        accordionPanelUnorderedList: {
            padding: "0 0 0 20px",
            paddingTop: "0",
        },
        accordionDocumentsWrapper: {
            padding: "0",
        },
        customproducttilewrapper: {
            padding: "0",
        },
        addToCartButton: {
            width: "100%",
            height: "48px",
        },
        addToProjectButton: {
            width: "100%",
            iconSize: 18,
        },
        msrpWrapper: {
            margin: "0",
        },
        msrpContainer: {
            color: "#494745",
            size: "13px",
            marginRight: "2px",
        },
        productPriceWrapper: {
            marginRight: "24px",
            marginTop: "0",
            marginBottom: "0",
        },
        dataPointContent: {
            size: "16px",
            weight: 400,
            letterSpacing: "0.5px",
            color: "#494745",
        },
        productDataPoint: {
            titleGridItem: {
                size: "32px",
                margin: "8px 0",
            },
            productName: {
                size: "32px",
                margin: "8px 0",
                weight: 100,
                lineHeight: "1.5",
            },
        },
        mobileViewAddToCartMobileDiv: {
            display: "block",
        },
        productAvailabilityQuantity: {
            desktop: {
                marginTop: "12px",
            },
            mobile: {
                marginTop: "8px",
            },
        },
        topGridItem: {
            desktop: {
                padding: "15px 20px 0 0",
                marginTop: "0",
            },
            mobile: {
                padding: "0",
                marginTop: "0",
            },
        },
        rightColumnGridItem: {
            desktop: {
                paddingLeft: "20px",
            },
            mobile: {
                paddingLeft: "0",
            },
        },
        priceContainer: {
            marginTop: "16px",
            marginBottom: "16px",
        },
        productImageGallery: {
            wrapperMaxHeight: "656px",
            mainWrapperBg: "transparent",
            thumbBg: "transparent",
        },
    },
    breadcrumbs: {
        padding: "12px 0",
    },
    productImage: {
        padding: "0",
    },
    pagination: {
        padding: "4px 12px",
        size: "16px",
        minHeight: "unset",
    },
    categoryDetailBanner: {
        textAlign: "center",
        color: "#494745",
        maxWidth: "700px",
        size: "14px",
        letterSpacing: "0.5px",
        lineHeight: "1.5",
        margin: "8px auto 0",
    },
    categoryListing: {
        mainContentContainer: {
            marginTop: "32px",
        },
        pageTitle: {
            marginBottom: "0",
        },
    },
};

const WIDGETS = {
    myListsDetails: {
        quickAddMobileLeftPadding: "5px",
        quickAddCloseIconMobileMarginTop: "21px",
    },
    promotionSummary: {
        fontFamily: "'Mulish', sans-serif",
        discountFontSize: "14px",
        discountValueFontSize: "15px",
        labelFontSize: "15px",
        valueFontSize: "15px",
        messageFontSize: "13px",
        messageWhiteSpace: "nowrap",
        savingsColor: COLORS.success,
        messageColor: "#777777",
        calloutBackground: "#EFF8F1",
        calloutBorder: "#DCEFE0",
        calloutTextColor: COLORS.success,
        calloutMarginTop: "4px",
        calloutGap: "8px",
        calloutPadding: "10px 12px",
        calloutIconSize: "16px",
        calloutFontSize: "13px",
        calloutLineHeight: "1.4",
        dividerColor: "#E0E0E0",
        dividerMargin: "4px 0",
        estimatedTotalFontSize: "15px",
        estimatedTotalFontWeight: 700,
        estimatedTotalLineHeight: "1.4",
        estimatedTotalHelperFontSize: "13px",
        estimatedTotalHelperLineHeight: "1.4",
        estimatedTotalHelperMarginTop: "2px",
    },
    checkoutNavigation: {
        iconWidth: "21px",
        iconHeight: "8px",
        iconGap: "8px",
        backButtonColor: COLORS.text.link,
        desktopBackMarginTop: "30px",
    },
    checkoutPaymentInformation: {
        hostedPaymentField: {
            height: "48px",
            inlineEndInset: "1px",
        },
        summaryAllocation: {
            borderTop: `1px solid ${COLORS.border.main}`,
            margin: "0 20px",
            padding: "12px 0 16px",
            rowGap: "8px",
            labelAmountGap: "12px",
            fontSize: "14px",
            fontWeight: 700,
            lineHeight: "1.4",
            dueTodayColor: COLORS.success,
            onTermsColor: COLORS.warning,
        },
        paymentBreakdown: {
            optionGap: "12px",
            optionBorder: `1px solid ${COLORS.border.main}`,
            optionSelectedBorder: `1px solid ${COLORS.accent.main}`,
            optionSelectedBoxShadow: `0 0 0 1px ${COLORS.accent.main}`,
            boxBorderRadius: "6px",
            optionHeaderPadding: "16px 20px 16px 28px",
            optionHeaderMobilePadding: "14px 12px 14px 20px",
            optionLabelGap: "16px",
            optionTextMinWidth: "180px",
            optionTitleFontWeight: 700,
            optionTitleLineHeight: "1.3",
            optionSubtitleFontSize: "12px",
            optionSubtitleLineHeight: "1.3",
            optionAmountFontSize: "18px",
            optionAmountMobileFontSize: "15px",
            optionAmountFontWeight: 700,
            optionAmountLineHeight: "1.3",
            selectedContentGap: "8px",
            selectedContentPadding: "0 20px 20px 40px",
            selectedContentMobilePadding: "0 12px 16px 16px",
            chargedTodayColor: COLORS.success,
            onTermsColor: COLORS.warning,
            compactRowGap: "16px",
            compactRowPadding: "12px 16px",
            compactRowMobilePadding: "12px",
            compactRowBackground: COLORS.checkout.lightBg,
            compactRowAccentWidth: "4px",
            compactMetaFontSize: "11px",
            compactMetaFontWeight: 700,
            compactMetaLetterSpacing: "0.06em",
            compactMetaLineHeight: "1.3",
            compactLabelMargin: "2px 0 0",
            compactLabelFontSize: "12px",
            compactLabelLineHeight: "1.3",
            compactAmountFontSize: "16px",
            compactAmountFontWeight: 700,
            compactAmountLineHeight: "1.3",
            disclosureMinHeight: "44px",
            disclosurePadding: "8px 0",
            disclosureColor: COLORS.text.link,
            disclosureFontSize: "13px",
            disclosureFontWeight: 700,
            disclosureLineHeight: "1.4",
            disclosureUnderlineOffset: "3px",
            disclosureFocusOutline: `2px solid ${COLORS.accent.main}`,
            disclosureFocusOutlineOffset: "2px",
            expandedContentPaddingTop: "4px",
            detailSectionGap: "12px",
            detailSectionMinWidth: "240px",
            detailBorder: `1px solid ${COLORS.border.main}`,
            detailHeaderGap: "12px",
            detailHeaderPadding: "10px 12px",
            detailHeaderBackground: COLORS.checkout.lightBg,
            detailHeaderAccentWidth: "4px",
            detailHeaderFontWeight: 700,
            detailTagFontSize: "11px",
            detailTagFontWeight: 700,
            detailTagLetterSpacing: "0.06em",
            detailBodyPadding: "12px",
            detailLabelFontSize: "11px",
            detailLabelLetterSpacing: "0.06em",
            detailLabelMarginBottom: "10px",
            detailRowGap: "12px",
            detailRowMarginBottom: "6px",
            detailTotalPaddingTop: "12px",
            detailTotalBorder: `1px solid ${COLORS.border.main}`,
            detailTotalFontWeight: 700,
            finalTotalGap: "12px",
            finalTotalMarginTop: "12px",
            finalTotalPaddingTop: "12px",
            finalTotalBorder: `1px solid ${COLORS.border.main}`,
            finalTotalFontWeight: 700,
            finalTotalAmountFontSize: "18px",
            additionalMessagePaddingTop: "12px",
            additionalMessageBorder: `1px solid ${COLORS.border.main}`,
            additionalMessageFontSize: "12px",
        },
        termsMethod: {
            background: COLORS.checkout.lightBg,
            border: `1px solid ${COLORS.border.main}`,
            borderRadius: "4px",
            gap: "4px",
            padding: "14px 16px",
        },
        termsMethodLabel: {
            color: COLORS.checkout.text.secondary,
            fontSize: "12px",
            fontWeight: 700,
            lineHeight: "1.3",
        },
        termsMethodValue: {
            color: COLORS.checkout.text.primary,
            fontSize: "16px",
            fontWeight: 700,
            lineHeight: "1.3",
        },
        termsMethodHelper: {
            color: COLORS.checkout.text.secondary,
            fontSize: "12px",
            lineHeight: "1.3",
        },
    },
    announcementBar: {
        defaultBackground: "#000000",
        defaultTextColor: "#FFFFFF",
    },
    imageGrid: {
        imageWidth: [12, 12, 12, 7, 7] as const,
        contentWidth: [12, 12, 12, 5, 5] as const,
        imageMaxWidth: "none",
        contentMaxWidth: "none",
        imageFlexBasis: "60%",
        contentFlexBasis: "40%",
        padding: {
            left: "40px",
            right: "40px",
        },
        mobileXPadding: "16px",
        image: {
            aspectRatio: "1.36",
        },
        contentTitle: {
            variant: "h1" as const,
            fontSize: "60px",
            responsiveFontSizes: ["32px", "32px", "32px", null, null],
        },
        preTitle: {
            letterSpacing: "2px",
        },
    },
    featuredCategory: {
        slideTitle: {
            weight: 400,
            textTransform: "none",
        },
    },
    heroCarousel: {
        slideTitle: {
            size: "64px",
            lineHeight: "80px",
        },
    },
    tabGroup: {
        wrapper: {
            padding: "4px 0",
        },
    },
    productListDrawer: {
        size: undefined,
        width: "100vw",
        top: "0",
        title: {
            letterSpacing: "1px",
        },
    },
};

const COMMON = {
    ...SURYA_COMMON,
    breakpoints: [0, 576, 768, 992, 1440],
    width: {
        ...SURYA_COMMON.width,
        content: 1360,
    },
    layout: {
        mobileSpacing: "32px",
        tabletSpacing: "32px",
        desktopSpacing: "80px",
    },
};

const Z_INDEX = {
    popover: 1260,
    datePicker: 1270,
    dynamicDropdown: 1280,
    stickyFooter: 1284,
    loadingOverlay: 1285,
    menu: 1290,
    drawer: 1300,
    modal: 1300,
    tabGroup: 400,
    toaster: 1400,
    shellPopover: 1310,
};

const COMPONENTS = {
    pagination: {
        defaultProps: {
            navIconsSrc: {
                firstPage: "ChevronsLeft",
                previousPage: GVLeftArrow,
                nextPage: GVRightArrow,
                lastPage: "ChevronsRight",
            },
        },
    },
};

export { COLORS, TYPOGRAPHY, SPACING, PRODUCT, COMMON, MENU, FORM, ELEMENTS, WIDGETS, Z_INDEX, COMPONENTS };
